import { useEffect, useState } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { ListChecks } from "@phosphor-icons/react";
import { EmptyState, PageHeader, Pager, SearchInput, Select, Table, TBody, TD, TH, THead, TR } from "@ghezaa/ui";
import { formatDateTime, listSearch, useCursorPages, useDebounced, type ListSearch } from "@ghezaa/shell";
import { useLog } from "@/api/queries";
import { useMarket } from "@/lib/market";

const PAGE = 50;

export type LogSearch = ListSearch & { market?: string };

export function logSearch(search: Record<string, unknown>): LogSearch {
  return { ...listSearch(search), ...(typeof search.market === "string" && search.market !== "" ? { market: search.market } : {}) };
}

/** Who set up, linked and changed what in Sales, newest first. */
export function LogPage() {
  const { markets } = useMarket();
  const search = useSearch({ strict: false }) as LogSearch;
  const navigate = useNavigate();
  const [q, setQ] = useState(search.q ?? "");
  const typed = useDebounced(q.trim());
  const setSearch = (patch: LogSearch) => void navigate({ to: ".", search: (prev: Record<string, unknown>) => ({ ...prev, ...patch }) as LogSearch, replace: true });

  useEffect(() => {
    if ((search.q ?? "") !== typed) setSearch({ q: typed || undefined, cursor: undefined });
  }, [typed]); // eslint-disable-line react-hooks/exhaustive-deps

  const pages = useCursorPages({ key: `log:${search.q ?? ""}:${search.market ?? ""}`, cursor: search.cursor, take: PAGE, onCursor: (cursor) => setSearch({ cursor }) });
  const log = useLog({ q: search.q, market: search.market, cursor: search.cursor, take: PAGE });
  const rows = log.data?.items ?? [];
  const filtered = Boolean(search.q || search.market);

  return (
    <div className="flex flex-col gap-6">
      <PageHeader title="Log" />
      <div className="flex flex-wrap items-center gap-2">
        <SearchInput placeholder="Search what or who" aria-label="Search the log" value={q} onChange={(e) => setQ(e.target.value)} className="max-w-xs" />
        {markets.length > 1 && (
          <Select aria-label="Market" value={search.market ?? ""} onChange={(e) => setSearch({ market: e.target.value || undefined, cursor: undefined })} className="w-auto">
            <option value="">Every market</option>
            {markets.map((m) => (
              <option key={m.code} value={m.code}>
                {m.name}
              </option>
            ))}
          </Select>
        )}
      </div>
      {log.isError && <p className="bg-danger-soft px-3 py-2 text-sm text-danger">Could not load the log: {log.error.message}</p>}
      {log.data && rows.length === 0 ? (
        <EmptyState icon={ListChecks} title={filtered ? "Nothing matches" : "Nothing logged yet"} description={filtered ? "Try other filters, or clear them." : "Setting up a market, linking a company and linking people appear here."} />
      ) : (
        log.data && (
          <div>
            <Table density="compact">
              <THead>
                <TR>
                  <TH>When</TH>
                  <TH>What</TH>
                  <TH from="md">Who</TH>
                  <TH from="lg">Market</TH>
                </TR>
              </THead>
              <TBody>
                {rows.map((e) => (
                  <TR key={e.id}>
                    <TD className="whitespace-nowrap text-xs tabular-nums">{formatDateTime(e.at)}</TD>
                    <TD className="w-full max-w-0">
                      <span className="block truncate" title={e.summary}>{e.summary}</span>
                    </TD>
                    <TD from="md" className="whitespace-nowrap">{e.who}</TD>
                    <TD from="lg" className="whitespace-nowrap font-mono text-xs">{e.market}</TD>
                  </TR>
                ))}
              </TBody>
            </Table>
            <Pager
              from={pages.from}
              shown={rows.length}
              total={log.data.total ?? null}
              hasPrevious={pages.hasPrevious}
              hasNext={Boolean(log.data.next)}
              onPrevious={pages.previous}
              onNext={() => log.data?.next && pages.next(log.data.next)}
              loading={log.isFetching}
              className="mt-3"
            />
          </div>
        )
      )}
    </div>
  );
}
